import { developmentDetector } from './developmentModeDetector';
import { globalScriptBlocker } from './globalScriptBlocker';

/**
 * Nuclear Shutdown Controller - Ultra Silent Mode
 * Master control for complete external call elimination - zero logging
 */

class NuclearShutdownController {
  private config = developmentDetector.getConfig();
  private isInitialized = false;

  constructor() {
    if (this.config.isDevelopment) {
      this.initializeNuclearShutdown();
    }
  }

  private initializeNuclearShutdown(): void {
    if (this.isInitialized) return;

    // Complete silence - no logging
    this.blockConsoleErrors();
    this.preventResourceLoading();
    this.sanitizeExistingElements();
    
    this.isInitialized = true;
  }

  private blockConsoleErrors(): void {
    // Suppress external script errors to clean up console
    const originalError = console.error;
    console.error = (...args: any[]) => {
      const message = args[0]?.toString() || '';
      
      // Always allow auth-related errors - they're critical for debugging
      const authPatterns = [
        'supabase',
        'auth',
        'session',
        'login',
        'Auth timeout',
        'Auth initialization'
      ];
      
      const isAuthRelated = authPatterns.some(pattern => 
        message.toLowerCase().includes(pattern.toLowerCase())
      );
      
      if (isAuthRelated) {
        originalError.apply(console, args);
        return;
      }
      
      // Block known external script errors
      const blockedErrors = [
        'ERR_TIMED_OUT',
        'facebook.com',
        'connect.facebook.net',
        'googletagmanager.com',
        'google-analytics.com',
        'tiktok.com',
        'reddit.com',
        'twitter.com',
        'linkedin.com',
        'Loading chunk',
        'Loading CSS chunk'
      ];

      const shouldBlock = blockedErrors.some(pattern => 
        message.includes(pattern)
      );

      if (shouldBlock) {
        // Silently block external errors to keep console clean
        return;
      }

      // Allow other errors through
      originalError.apply(console, args);
    };
  }

  private preventResourceLoading(): void {
    // Override createElement to block external scripts
    const originalCreateElement = document.createElement;
    document.createElement = function<K extends keyof HTMLElementTagNameMap>(
      tagName: K,
      options?: ElementCreationOptions
    ): HTMLElementTagNameMap[K] {
      const element = originalCreateElement.call(this, tagName, options);
      
      if (tagName.toLowerCase() === 'script') {
        const scriptElement = element as unknown as HTMLScriptElement;
        
        // Override src setter
        let originalSrc = '';
        Object.defineProperty(scriptElement, 'src', {
          get: () => originalSrc,
          set: (value: string) => {
            if (globalScriptBlocker.shouldBlockUrl(value)) {
              // Silently block without logging
              return;
            }
            originalSrc = value;
            scriptElement.setAttribute('src', value);
          }
        });
      }
      
      return element;
    };
  }

  private sanitizeExistingElements(): void {
    // Remove any existing external elements - silently
    const selectors = [
      'script[src*="facebook"]',
      'script[src*="google"]',
      'script[src*="tiktok"]',
      'script[src*="reddit"]',
      'script[src*="twitter"]',
      'script[src*="linkedin"]',
      'iframe[src*="facebook"]',
      'iframe[src*="google"]',
      'img[src*="facebook.com/tr"]',
      'link[href*="google"]'
    ];

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        element.remove();
      });
    });

    // Silent cleanup - no logging
  }

  public getStatus(): object {
    return {
      isInitialized: this.isInitialized,
      isDevelopment: this.config.isDevelopment,
      blockedDomains: globalScriptBlocker.getBlockedDomains(),
      config: this.config
    };
  }
}

export const nuclearShutdown = new NuclearShutdownController();

// Make available for debugging
if (typeof window !== 'undefined') {
  (window as any).nuclearShutdown = nuclearShutdown;
}
